import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { API_BASE } from "@/lib/api";
import { Menu, Settings as SettingsIcon, Sun, Moon, Monitor } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function TopBar({ onMenu }) {
  const { user } = useAuth();
  const { t } = useLang();
  const { theme, setTheme } = useTheme();

  const avatarUrl = user?.avatar_id ? `${API_BASE}/files/${user.avatar_id}` : null;
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-20 h-16 bg-[hsl(var(--surface))] border-b border-border flex items-center px-4 sm:px-6 gap-2 sm:gap-3"
    >
      {/* Hamburger — mobile only */}
      <button
        type="button"
        data-testid="mobile-menu-btn"
        onClick={onMenu}
        aria-label="Open menu"
        className="lg:hidden w-9 h-9 rounded-md border border-border bg-[hsl(var(--surface))] hover:bg-[hsl(var(--panel-alt))] flex items-center justify-center text-foreground"
      >
        <Menu className="w-4 h-4" strokeWidth={2} />
      </button>

      {/* Brand on mobile (sidebar is hidden) */}
      <Link
        to="/"
        className="lg:hidden flex items-center gap-2 min-w-0"
        aria-label="Home"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_complaint-lifecycle-1/artifacts/utuwjsdw_ChatGPT%20Image%20Jul%204%2C%202026%20at%2002_54_59%20PM.png"
          alt=""
          className="w-8 h-8 rounded-full object-cover ring-1 ring-border"
        />
        <span className="text-sm font-bold text-foreground truncate">
          Office of RSB
        </span>
      </Link>

      <div className="flex-1" />

      {/* Quick theme toggle */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="theme-toggle-btn"
            className="w-9 h-9 rounded-md border border-border bg-[hsl(var(--surface))] hover:bg-[hsl(var(--panel-alt))] flex items-center justify-center text-foreground"
            aria-label="Theme"
          >
            <ThemeIcon className="w-4 h-4" strokeWidth={2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
            Theme
          </DropdownMenuLabel>
          <DropdownMenuItem
            data-testid="theme-set-light"
            onClick={() => setTheme("light")}
            className={theme === "light" ? "bg-accent/10 text-accent" : ""}
          >
            <Sun className="w-4 h-4 mr-2" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="theme-set-dark"
            onClick={() => setTheme("dark")}
            className={theme === "dark" ? "bg-accent/10 text-accent" : ""}
          >
            <Moon className="w-4 h-4 mr-2" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="theme-set-system"
            onClick={() => setTheme("system")}
            className={theme === "system" ? "bg-accent/10 text-accent" : ""}
          >
            <Monitor className="w-4 h-4 mr-2" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Settings icon */}
      <Link
        to="/settings"
        data-testid="topbar-settings"
        aria-label={t("nav.settings")}
        className="w-9 h-9 rounded-md border border-border bg-[hsl(var(--surface))] hover:bg-[hsl(var(--panel-alt))] flex items-center justify-center text-foreground transition-colors"
      >
        <SettingsIcon className="w-4 h-4" strokeWidth={2} />
      </Link>

      {/* User chip */}
      <Link
        to="/settings"
        data-testid="topbar-user-chip"
        className="flex items-center gap-2 sm:gap-3 pl-1.5 pr-2 sm:pl-2 sm:pr-3 h-10 rounded-md border border-border bg-[hsl(var(--surface))] hover:bg-[hsl(var(--panel-alt))] transition-colors"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={user?.name}
            className="w-7 h-7 rounded-full object-cover ring-2 ring-border"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[hsl(var(--panel-alt))] text-foreground text-[11px] font-bold flex items-center justify-center">
            {initials}
          </div>
        )}
        <div className="hidden sm:block leading-tight text-left">
          <div className="text-xs font-semibold text-foreground truncate max-w-[140px]">
            {user?.name}
          </div>
          <div className="text-[10px] uppercase font-mono-cms text-muted-foreground">
            {user?.role}
          </div>
        </div>
      </Link>
    </header>
  );
}
